import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type { Deal } from '@/types'
import { dateInputToIso, toDateInputValue } from './legalDataUtils'

export function useLegalDataSection(deal: Deal, onDealPatched: (updates: Partial<Deal>) => void) {
  const [emailFinanceiro, setEmailFinanceiro] = useState('')
  const [representanteLegal, setRepresentanteLegal] = useState('')
  const [assinadoEm, setAssinadoEm] = useState('')
  const [inicioContrato, setInicioContrato] = useState('')
  const [primeiroPagamento, setPrimeiroPagamento] = useState('')
  const [empresaCnpj, setEmpresaCnpj] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setAssinadoEm(toDateInputValue(deal.signedAt))
    setInicioContrato(toDateInputValue(deal.contractStartDate ?? deal.contractStart))
    setPrimeiroPagamento(toDateInputValue(deal.firstPaymentDate))
  }, [deal.id, deal.signedAt, deal.contractStartDate, deal.contractStart, deal.firstPaymentDate])

  useEffect(() => {
    if (loaded) return
    let cancelled = false

    async function load() {
      try {
        const requests: Promise<unknown>[] = [api.get(`/deals/${deal.id}`)]
        if (deal.empresaId) requests.push(api.get(`/empresas/${deal.empresaId}`))

        const [dealRes, empresaRes] = await Promise.allSettled(requests)
        if (cancelled) return

        if (dealRes.status === 'fulfilled') {
          const payload = dealRes.value as { data?: { data?: Deal } & Deal }
          const fresh = payload.data?.data ?? payload.data
          if (fresh) {
            setAssinadoEm(toDateInputValue(fresh.signedAt))
            setInicioContrato(toDateInputValue(fresh.contractStartDate ?? fresh.contractStart))
            setPrimeiroPagamento(toDateInputValue(fresh.firstPaymentDate))
          }
        }

        if (empresaRes?.status === 'fulfilled') {
          const payload = empresaRes.value as { data?: { data?: Record<string, string> } & Record<string, string> }
          const empresa = payload.data?.data ?? payload.data
          if (empresa && typeof empresa === 'object') {
            if (empresa.cnpj) setEmpresaCnpj(String(empresa.cnpj))
            setRepresentanteLegal(String(empresa.responsavelAssinaturaNome ?? ''))
            setEmailFinanceiro(String(empresa.responsavelFinanceiroEmail ?? ''))
          }
        }
      } catch {
        // dados legais ainda não preenchidos
      } finally {
        if (!cancelled) setLoaded(true)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [deal.id, deal.empresaId, loaded])

  const persist = useCallback(async () => {
    setSaving(true)
    try {
      const signedAt = dateInputToIso(assinadoEm)
      const contractStartDate = dateInputToIso(inicioContrato)
      const firstPaymentDate = dateInputToIso(primeiroPagamento)

      await api.patch(`/deals/${deal.id}`, {
        signedAt: signedAt ?? undefined,
        contractStartDate: contractStartDate ?? undefined,
        firstPaymentDate: firstPaymentDate ?? undefined,
      })

      if (deal.empresaId) {
        await api.patch(`/empresas/${deal.empresaId}`, {
          responsavelAssinaturaNome: representanteLegal.trim() || undefined,
          responsavelFinanceiroEmail: emailFinanceiro.trim() || undefined,
        })
      }

      onDealPatched({
        signedAt: signedAt ?? undefined,
        contractStartDate: contractStartDate ?? undefined,
        contractStart: contractStartDate ?? undefined,
        firstPaymentDate: firstPaymentDate ?? undefined,
        representanteLegal: representanteLegal.trim() || undefined,
        emailFinanceiro: emailFinanceiro.trim() || undefined,
      })
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao salvar dados legais'))
    } finally {
      setSaving(false)
    }
  }, [
    assinadoEm,
    deal.empresaId,
    deal.id,
    emailFinanceiro,
    inicioContrato,
    onDealPatched,
    primeiroPagamento,
    representanteLegal,
  ])

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      void persist()
    }, 300)
  }, [persist])

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  return {
    emailFinanceiro,
    setEmailFinanceiro,
    representanteLegal,
    setRepresentanteLegal,
    assinadoEm,
    setAssinadoEm,
    inicioContrato,
    setInicioContrato,
    primeiroPagamento,
    setPrimeiroPagamento,
    empresaCnpj,
    saving,
    scheduleSave,
    persist,
  }
}
